import WaitingGhost from './ui/WaitingGhost';
import AskAiButton from './aiChat/AskAiButton';
import { buildSqlErrorPrompt } from '../services/ai/failurePrompts';
import {
  EMPTY_COLUMN_WIDTHS, EMPTY_FIND_KEYS, EMPTY_HIDDEN_COLUMNS, EMPTY_PENDING_DELETES,
  EMPTY_SORT_LEVELS,
} from './dbGridModel';
import { EmptyState } from './dbGridCells';
import { renderResultsGrid } from './dbManager/renderResultsGrid';
import { gridRowRendering } from './dbManager/gridRowRendering';
import { useGridViewport } from './dbManager/useGridViewport';
export { __resetPinnedWidths } from './dbManager/resultsGridLayout';

export default function ResultsGrid({
  loading,
  /** Pre-formatted elapsed time for the running query, e.g. "1.4 s". */
  elapsedLabel = null,
  error,
  // The statement behind `error`, plus the connection it ran on — used only
  // by the inline "Explain this error" affordance.
  failedSql = null,
  connectionName = null,
  result, canAddRow, editable, pendingRows, pendingEdits,
  // Row indexes marked for deletion but not yet saved. Drawn red; Save commits
  // them, Escape drops them.
  pendingDeletes = EMPTY_PENDING_DELETES,
  editingCell, editingValue, onAddRow, onUpdatePendingCell, onRemovePendingRow, onCellOpen,
  onCellContextMenu, onBackgroundContextMenu, onEditingValueChange, onCommitEdit, onCancelEdit,
  onEditNull,
  // Tab pressed inside the cell editor. Optional: without it the key falls
  // through to the browser, which is what used to send focus past the table.
  onEditTab,
  sortable = false,
  sortState = EMPTY_SORT_LEVELS,
  onSortColumn, selectedRows, onToggleRowSelect, selectedCells, onCellSelect, onDatePick,
  rowNumberOffset = 0,
  columnTypes,
  // ENUM columns of a table tab: name → { values, nullable } (dbEnumColumns).
  // Their cells edit through a dropdown instead of the text input.
  enumByColumn,
  // Foreign keys declared on this table, indexed by the column they sit on.
  // Table tabs only: a query tab's columns are an arbitrary projection with no
  // constraint metadata to match against.
  fkByColumn,
  onFkNavigate,
  columnWidths = EMPTY_COLUMN_WIDTHS,
  onColumnResize,
  // Ctrl+F highlight: every match dimly, the one you are on brightly.
  findMatchKeys = EMPTY_FIND_KEYS,
  activeFindKey = null,
  cursorKey = null,
  onStop,
  stopTitle,
  // Row selection on a READ-ONLY grid (query tabs): selecting is a read — it
  // drives the record view, never a write.
  rowSelectable = false,
  // Columns not drawn. The arrays are NOT filtered — every `j` below stays the
  // true index into result.columns, so pending edits, FK lookups and the
  // context menu keep addressing the same cells they did before anything was
  // hidden.
  hiddenColumns = EMPTY_HIDDEN_COLUMNS,
  onHeaderContextMenu,
  // Called when the viewport is near the bottom, so the tab can fetch the next
  // chunk. Absent on read-only grids, which hold everything they will ever
  // show.
  onScrollNearEnd,
  loadingMore = false,
  // Scroll position, remembered per tab so a restored tab comes back where it
  // was left rather than at the top.
  onScrollPosition,
  initialScrollTop = 0,
  scrollRestoreKey,
  // True only when this restore follows a record-view flip, where "where was I"
  // is the selected row rather than a pixel offset. Defaults false so a caller
  // that does not pass it — or a test — gets the plain offset restore.
  anchorToSelection = false,
}) {
  const {
    scrollHostRef, anchorCell, focusAnchorCell, virtualRows, windowed, padTop, padBottom,
    hasColumnWidths, tableElRef, typeColors, beginResize, guideNode,
  } = useGridViewport({
    result, loading, error, pendingRows, editingCell, selectedRows, selectedCells, columnTypes,
    columnWidths, onColumnResize, activeFindKey, cursorKey, hiddenColumns, onScrollNearEnd,
    onScrollPosition, initialScrollTop, scrollRestoreKey, anchorToSelection,
  });

  if (loading) {
    // The same waiting state as the API Client's "Awaiting response" (owner,
    // 07.10.2026: one component for both — ui/WaitingGhost). The clock sits in
    // the meta line under the ghost, not among the toolbar buttons (a ticking
    // number in a row of controls reads as a disabled button), and this Stop
    // is the only Stop (owner, 01.10.2026).
    return (
      <div className="dbm-empty dbm-running">
        <WaitingGhost
          title="Running query"
          meta={elapsedLabel ? <span className="dbm-elapsed--running">{elapsedLabel}</span> : null}
          onCancel={onStop || undefined}
          cancelLabel="Stop"
          cancelHint={stopTitle || 'Cancel this query'}
          aria-label="Loading"
        />
      </div>
    );
  }
  if (error) {
    return (
      <div className="dbm-error">
        <div>{error}</div>
        {/* The error string alone is not actionable — "syntax error at or near
            FORM" means nothing without the statement — so the prompt carries
            the SQL that produced it. */}
        {failedSql && (
          <div className="dbm-error__actions">
            <AskAiButton
              label="Explain this error"
              title="Ask the AI assistant what is wrong with this query"
              prompt={buildSqlErrorPrompt({ error, sql: failedSql, connectionName })}
            />
          </div>
        )}
      </div>
    );
  }
  if (!result) return <EmptyState>No results yet</EmptyState>;
  if (result.columns.length === 0) return <EmptyState>Query returned no columns</EmptyState>;

  const {
    isHidden, colCount, canSelect, cellMouseDown, cellClass, totalResultRows, renderRows,
    pendingUnderRow, trailingPending, renderPendingRow,
  } = gridRowRendering({
    result, editable, pendingRows, onUpdatePendingCell, onRemovePendingRow, selectedRows,
    onToggleRowSelect, selectedCells, onCellSelect, onDatePick, columnTypes, enumByColumn,
    rowSelectable, hiddenColumns, windowed, virtualRows,
  });

  return renderResultsGrid({
    result, canAddRow, editable, pendingRows, pendingEdits, pendingDeletes, editingCell,
    editingValue, onAddRow, onCellOpen, onCellContextMenu, onBackgroundContextMenu,
    onEditingValueChange, onCommitEdit, onCancelEdit, onEditNull, onEditTab, sortable, sortState,
    onSortColumn, selectedRows, onToggleRowSelect, selectedCells, onDatePick, rowNumberOffset,
    columnTypes, enumByColumn, fkByColumn, onFkNavigate, columnWidths, onColumnResize,
    findMatchKeys, activeFindKey, onHeaderContextMenu, loadingMore, scrollHostRef, anchorCell,
    focusAnchorCell, padTop, padBottom, hasColumnWidths, tableElRef, typeColors, beginResize,
    guideNode, colCount, isHidden, canSelect, cellMouseDown, cellClass, totalResultRows, renderRows,
    pendingUnderRow, trailingPending, renderPendingRow,
  });
}
